import * as React from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import type { EmailOtpType } from "@supabase/supabase-js";
import { setPasswordSchema } from "@taskiq/shared";
import { AuthLayout } from "@/components/AuthLayout";
import { Button } from "@/components/ui/Button";
import { Field, PasswordInput, Form } from "@/components/ui/Field";
import { FormMessage, Spinner } from "@/components/ui/Feedback";
import { useAuth } from "@/lib/auth-context";
import { describeAuthError, fieldErrorsFrom } from "@/lib/auth-errors";
import { supabase } from "@/lib/supabase";

type Mode = "reset" | "invite";

const COPY: Record<
  Mode,
  { title: string; subtitle: string; submit: string; expired: string; retry: React.ReactNode }
> = {
  reset: {
    title: "Set a new password",
    subtitle: "Choose something you have not used before.",
    submit: "Save password",
    expired: "Password reset links are valid for one hour and can only be used once.",
    retry: (
      <Link to="/forgot-password">
        <Button>Send a new link</Button>
      </Link>
    ),
  },
  invite: {
    title: "Choose a password",
    subtitle: "Set a password and your TaskIQ account is ready to use.",
    submit: "Create my account",
    expired: "Invitation links expire. Ask your administrator to send you a new one.",
    retry: (
      <Link to="/login">
        <Button variant="secondary">Back to sign in</Button>
      </Link>
    ),
  },
};

/**
 * Set a password. Serves two arrivals that behave identically once they get
 * here: `/reset-password` from a forgotten password, and `/accept-invite`, which
 * is how an invited account is first activated, because an invitee is created
 * with no password at all.
 *
 * ── Opening the link must not consume the token ─────────────────────────────
 *
 * Both emails point **straight here**, carrying `?token_hash=`. This page does
 * not redeem it on load. It renders the password form against a token it has not
 * checked, and redeems it only when the form is submitted:
 *
 *     GET  /accept-invite?token_hash=…   →  form renders, nothing consumed
 *     submit                             →  verifyOtp (consumes) → updateUser
 *
 * That ordering is the whole design. The obvious alternative - redeem on arrival,
 * then show the form - turns a GET into the state change, and mail security
 * scanners follow every link in an incoming message. The token is burned before
 * the human clicks, and they get "this link has expired" on a link a minute old.
 * Rendering first means a scanner receives static HTML and changes nothing.
 *
 * The cost is honest and small: an invalid token cannot be reported until the
 * password has been typed. There is nowhere to check it that does not also
 * spend it - GoTrue has no validate-without-consuming endpoint.
 *
 * ── A rejected password does not spend the link ─────────────────────────────
 *
 * The two calls above fail independently, and `updateUser` failing is the common
 * one: the server's password policy is not visible from here, so `weak_password`
 * comes back on a password `setPasswordSchema` accepted. Pressing the button
 * again used to fail with `otp_expired` - the token had gone on the first press.
 * `redeemAndSetPassword` now redeems each token at most once and reuses the
 * session for the retry, so the form can be resubmitted until a password sticks.
 * The header on that function has the details, including why the record is keyed
 * on the resulting user id and not on the token alone.
 *
 * A live session with no token in the URL is also handled: that is a signed-in
 * user changing their own password.
 */
export function ResetPassword({ mode = "reset" }: { mode?: Mode }) {
  const copy = COPY[mode];
  const { redeemAndSetPassword, updatePassword, signOut } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const tokenHash = params.get("token_hash");
  const otpType: Extract<EmailOtpType, "invite" | "recovery"> =
    mode === "invite" ? "invite" : "recovery";

  const [ready, setReady] = React.useState(false);
  const [hasSession, setHasSession] = React.useState(false);
  const [values, setValues] = React.useState({ password: "", confirmPassword: "" });
  const [fieldErrors, setFieldErrors] = React.useState<Record<string, string>>({});
  const [formError, setFormError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    // With a token in hand there is nothing to wait for - the form is what the
    // token is for, and checking it here is exactly what this page must not do.
    if (tokenHash) {
      setReady(true);
      return;
    }

    let cancelled = false;

    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setHasSession(Boolean(data.session));
      setReady(true);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (session) {
        setHasSession(true);
        setReady(true);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, [tokenHash]);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = setPasswordSchema.safeParse(values);
    if (!parsed.success) {
      setFieldErrors(fieldErrorsFrom(parsed.error));
      return;
    }
    setFieldErrors({});
    setSubmitting(true);

    try {
      if (tokenHash) {
        await redeemAndSetPassword(tokenHash, otpType, parsed.data.password);
      } else {
        await updatePassword(parsed.data.password);
      }
      // Sign out deliberately. The session came from an emailed link, and anyone
      // who can read that inbox could replay it, so the new password gets used
      // at least once before there is a long-lived session on the account.
      await signOut();
      navigate(mode === "invite" ? "/login?welcome=1" : "/login?reset=1", { replace: true });
    } catch (err) {
      setFormError(describeAuthError(err));
      setSubmitting(false);
    }
  }

  if (!ready) {
    return (
      <AuthLayout title="One moment">
        <Spinner className="spinner--lg" />
      </AuthLayout>
    );
  }

  // No token and no session: they arrived here by hand, or the tab that held the
  // session is gone. Nothing to set a password against.
  if (!tokenHash && !hasSession) {
    return (
      <AuthLayout title="That link has expired" subtitle={copy.expired}>
        {copy.retry}
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={copy.title} subtitle={copy.subtitle}>
      <Form onSubmit={onSubmit}>
        <Field
          label={mode === "invite" ? "Password" : "New password"}
          htmlFor="password"
          required
          error={fieldErrors.password}
          hint="At least 10 characters."
        >
          <PasswordInput
            id="password"
            name="password"
            inputSize="md"
            autoComplete="new-password"
            autoFocus
            invalid={Boolean(fieldErrors.password)}
            value={values.password}
            onChange={(e) => setValues((v) => ({ ...v, password: e.target.value }))}
          />
        </Field>

        <Field
          label={mode === "invite" ? "Confirm password" : "Confirm new password"}
          htmlFor="confirmPassword"
          required
          error={fieldErrors.confirmPassword}
        >
          <PasswordInput
            id="confirmPassword"
            name="confirmPassword"
            inputSize="md"
            autoComplete="new-password"
            invalid={Boolean(fieldErrors.confirmPassword)}
            value={values.confirmPassword}
            onChange={(e) => setValues((v) => ({ ...v, confirmPassword: e.target.value }))}
          />
        </Field>

        <FormMessage>{formError}</FormMessage>

        <Button type="submit" size="lg" block loading={submitting} className="auth-submit">
          {copy.submit}
        </Button>
      </Form>
    </AuthLayout>
  );
}
